import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem } from '@mui/material';
import { ApiService, CompanyService, RetrieveDocumentGenerationJob } from 'api';
import { useResponse } from 'services/hooks';
import DocumentGenerationJobTableItemCapTableMenuProps from './DocumentGenerationJobTableItemCapTableMenu.props';
import { useStore } from '../../../../../../common/store';
import { UseStoreValues } from '../../../../../../common/types/store';
import { useAsyncProcessor } from '../../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { Logger } from '../../../../../../utilities';

const DocumentGenerationJobTableItemCapTableMenu = ({
  generationJob,
  onClose,
}: DocumentGenerationJobTableItemCapTableMenuProps) => {
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { successNotification, errorNotification } = useResponse();
  const { addJob, removeJob } = useAsyncProcessor();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;

  const hasMetadata = 
    generationJob.metadata &&
    generationJob.metadata.measurement_date_id;

  const handleApplyClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleApplyConfirm = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      if (!companyInfo?.id) {
        errorNotification('Company information not available');
        return;
      }

      Logger.info(`Applying cap table generation job ${generationJob.job_id}`);

      const result = (await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(companyInfo.id.toString(), {
        job_id: generationJob.job_id,
      })) as unknown as { security_ids: number[] };

      successNotification(`Cap table updated with ${result.security_ids?.length} securities from AI results`);

      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      Logger.error('Error applying cap table generation job:', err);
      const errorMessage = err?.body?.error || err?.message || 'Failed to apply cap table results';
      errorNotification(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };


  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
  };

  const isCompleted = generationJob.status === RetrieveDocumentGenerationJob.status.COMPLETED;

  return (
    <>
      <MenuItem onClick={handleApplyClick} disabled={loading || !isCompleted}>
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={16} />}
          {loading ? 'Applying...' : 'Apply to Cap Table'}
        </Box>
      </MenuItem>

      <Dialog open={confirmDialogOpen} onClose={handleConfirmCancel}>
        <DialogTitle>Apply AI Results to Cap Table</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will replace all existing securities on the cap table with the AI-extracted data.
            Existing securities will be removed.
            {!hasMetadata && (
              <>
                <br /><br />
                <strong>Note:</strong> No measurement date metadata was found on this job.
                The results will be applied to the most recent measurement date for this company.
              </>
            )}
            <br /><br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleApplyConfirm} color="primary" variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentGenerationJobTableItemCapTableMenu;
